<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import { useOrganizationStore } from '@/stores/organization';
import { useTeamStore } from '@/stores/team';
import type { Employee } from '@/types/employee';
import type { Team, TeamForm } from '@/types/team';
import { required } from '@/utils/validation';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const teamStore = useTeamStore();
const employeeStore = useEmployeeStore();

const isDialogOpen = defineModel<boolean>();
const emit = defineEmits(['fetchTeams', 'update:modelValue', 'teamCreated', 'teamUpdated']);
const employees = ref<Employee[]>([]);

const props = defineProps<{
    id?: string;
}>();

const form = useTemplateRef('form');
const team = ref<Team>({} as Team);
const isLoading = ref(false);
const dialogTitle = computed(() => (props.id ? 'Team bewerken' : 'Team aanmaken'));

onMounted(async () => {
    employees.value = await employeeStore.fetch();
});

watch(isDialogOpen, async () => {
    if (isDialogOpen.value === false) return;

    isLoading.value = true;
    team.value = {} as Team;

    if (props.id) {
        team.value = await teamStore.fetchById(props.id);

        if (team.value?._id !== props.id) {
            employees.value = await employeeStore.fetch();
        }
    }

    if (authStore.isSuperAdmin()) await organizationStore.get();

    isLoading.value = false;
});

function closeDialog() {
    team.value = {} as Team;
    form.value?.reset();
    isDialogOpen.value = false;
}

async function saveTeam() {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid || !team.value) return;

    isLoading.value = true;
    try {
        const teamForm: TeamForm = {
            ...team.value,
            teamleader: team.value.teamleader?._id ?? null,
            employees: team.value.employees?.map((e) => e._id.toString()) ?? []
        };

        if (props.id) {
            const result = await teamStore.update(teamForm);
            emit('teamUpdated', result.object);
        } else {
            const createdTeam = await teamStore.create(teamForm);
            emit('teamCreated', createdTeam);
        }

        closeDialog();
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
    <Dialog v-model="isDialogOpen" :title="dialogTitle" :isLoading="isLoading" persisent>
        <v-form ref="form">
            <v-text-field v-model="team.name" label="Naam" required :rules="[required]" />
            <v-select
                v-model="team.teamleader"
                label="Teamleider"
                :items="employees"
                item-title="fullName"
                item-value="_id"
                return-object
                clearable
            />
            <v-select
                v-if="authStore.isSuperAdmin()"
                v-model="team.originalOrganization"
                label="Bedrijf"
                required
                :rules="[required]"
                :items="organizationStore.organizations"
                item-title="name"
                item-value="_id"
            />
            <v-autocomplete
                label="Medewerkers"
                v-model="team.employees"
                :rules="[required]"
                :items="employees"
                item-title="fullName"
                item-value="_id"
                return-object
                multiple
                clearable
                required
            />
        </v-form>
        <template #actions>
            <v-spacer />
            <CustomButton class="mr-2" text="Opslaan" @click="saveTeam" />
            <CustomButton color="red" text="Annuleren" @click="closeDialog" />
        </template>
    </Dialog>
</template>
